import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui';

export default function Signup() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Signup is invite-only. Resolve the token up front so the page can say WHICH
  // organization is inviting and lock the email to the address it was issued to -
  // otherwise the first sign of trouble is a 403 after the form is filled in.
  const [invite, setInvite] = useState<{ org_name: string; email: string; role: string } | null>(null);
  const [inviteError, setInviteError] = useState('');
  const [checkingInvite, setCheckingInvite] = useState(Boolean(inviteToken));

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    api
      .verifyInvite(inviteToken)
      .then((res) => {
        if (cancelled) return;
        setInvite(res.invite);
        setEmail(res.invite.email);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setInviteError(err instanceof Error ? err.message : 'This invite link is not valid.');
      })
      .finally(() => !cancelled && setCheckingInvite(false));
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signup(email, password, fullName, inviteToken);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Signup failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src="/apex-logo.png" alt="Apex Real Estate Services" className="mx-auto h-9 w-auto" />
          <h2 className="mt-6 font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900">Create your account</h2>
          {invite ? (
            <p className="mt-2 text-sm text-gray-600">
              You have been invited to join{' '}
              <span className="font-semibold text-gray-900">{invite.org_name}</span> as {invite.role}.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-600">Start analyzing deals today</p>
          )}
        </div>

        {/* No token, or a bad one: say so before the form rather than after submitting. */}
        {!checkingInvite && !invite && (
          <div className="bg-warning-wash border border-warning/30 text-warning px-4 py-3 rounded-lg text-sm">
            {inviteError ||
              'Signing up requires an invitation. Ask an administrator to send you an invite link.'}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                /* Locked when an invite resolved: the server rejects a mismatch anyway
                   (one forwarded link must not admit anybody), so let the form say that
                   rather than fail on submit. */
                readOnly={Boolean(invite)}
                className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 ${invite ? 'bg-gray-50 text-gray-600' : ''}`}
                placeholder="you@example.com"
              />
              {invite && (
                <p className="mt-1 text-xs text-gray-500">
                  This invitation was issued to this address.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Minimum 8 characters"
              />
              <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            isLoading={loading}
            disabled={!invite || checkingInvite}
            className="w-full"
          >
            {loading ? 'Creating account...' : checkingInvite ? 'Checking invitation...' : 'Sign up'}
          </Button>

          <div className="text-center text-sm">
            <span className="text-gray-600">Already have an account? </span>
            <Link to="/login" className="font-medium text-primary-500 hover:text-primary-600">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
